/**
 * Owner → Owner accounts.
 *
 * Who can sign in to this console. Adding one used to mean a database seed;
 * now it is a form. The oldest account is PRIMARY and read-only — an owner has
 * no password reset and no support login above them, so one account has to be
 * un-removable or a mis-click locks everyone out of the install for good.
 */
import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { KeyRound, Pencil, Plus, Trash2 } from 'lucide-react';
import { del, get, post, put } from '@/lib/api';
import type { OwnerEnrolmentHandout } from '@his/shared';
import { DataTable, Dialog, PageHeader } from '@/components/ui/layout';
import { Alert, Badge, Button, Field, Input, Spinner, StatusBadge } from '@/components/ui/primitives';
import { dateTime } from '@/lib/utils';
import { OwnerConfirmDialog } from './OwnerConfirm';

interface OwnerAccount {
  id: string;
  name: string;
  email: string;
  isActive: boolean;
  isPrimary: boolean;
  hasEnrolled: boolean;
  lastLoginAt: string | null;
  createdAt: string;
}

type Pending =
  | { kind: 'create' }
  | { kind: 'edit'; owner: OwnerAccount }
  | { kind: 'toggle'; owner: OwnerAccount }
  | { kind: 'delete'; owner: OwnerAccount }
  | { kind: 'reset'; owner: OwnerAccount };

export function OwnerAccountsPage() {
  const queryClient = useQueryClient();
  const [pending, setPending] = useState<Pending | null>(null);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [handout, setHandout] = useState<(OwnerEnrolmentHandout & { email: string }) | null>(null);

  const owners = useQuery({
    queryKey: ['platform-owners'],
    queryFn: () => get<OwnerAccount[]>('/platform/owners'),
  });

  const rows = owners.data ?? [];
  const needsFields = pending?.kind === 'create' || pending?.kind === 'edit';
  const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
  const nameValid = name.trim().length >= 2;

  const title =
    pending?.kind === 'create'
      ? 'Add an owner'
      : pending?.kind === 'edit'
        ? 'Edit owner'
        : pending?.kind === 'toggle'
          ? pending.owner.isActive
            ? 'Deactivate this owner'
            : 'Reactivate this owner'
          : pending?.kind === 'reset'
            ? 'Reset this owner’s authenticator'
            : 'Delete this owner';

  return (
    <div className="space-y-4">
      <PageHeader
        title="Owner accounts"
        description="Who can sign in to this console. Every change asks for your authenticator code."
        actions={
          <Button
            onClick={() => {
              setName('');
              setEmail('');
              setPending({ kind: 'create' });
            }}
          >
            <Plus className="h-4 w-4" /> Add owner
          </Button>
        }
      />

      {owners.isLoading ? (
        <Spinner />
      ) : (
        <DataTable
          rows={rows}
          rowKey={(owner) => owner.id}
          columns={[
            {
              header: 'Name',
              sort: (owner) => owner.name,
              cell: (owner) => (
                <>
                  {owner.name}
                  {owner.isPrimary ? <Badge variant="default" className="ms-2">Primary</Badge> : null}
                </>
              ),
            },
            { header: 'Email', sort: (owner) => owner.email, cell: (owner) => owner.email },
            { header: 'Status', sort: (owner) => owner.isActive, cell: (owner) => <StatusBadge status={owner.isActive ? 'active' : 'inactive'} /> },
            {
              header: 'Authenticator',
              sort: (owner) => owner.hasEnrolled,
              cell: (owner) =>
                owner.hasEnrolled ? (
                  <span className="text-xs text-muted-foreground">Paired</span>
                ) : (
                  <Badge variant="warning">
                    <KeyRound className="h-3 w-3" /> Not paired yet
                  </Badge>
                ),
            },
          ]}
          rowActions={(owner) =>
            owner.isPrimary ? (
              <span
                className="text-xs text-muted-foreground"
                title="The oldest owner account is the account of last resort — it cannot be changed or removed, so this install can never be locked out."
              >
                Protected
              </span>
            ) : (
              <>
                <Button
                  size="xs"
                  variant="ghost"
                  onClick={() => {
                    setName(owner.name);
                    setEmail(owner.email);
                    setPending({ kind: 'edit', owner });
                  }}
                >
                  <Pencil className="h-3.5 w-3.5" /> Edit
                </Button>
                <Button size="xs" variant="ghost" onClick={() => setPending({ kind: 'toggle', owner })}>
                  {owner.isActive ? 'Deactivate' : 'Reactivate'}
                </Button>
                <Button size="xs" variant="ghost" onClick={() => setPending({ kind: 'reset', owner })}>
                  <KeyRound className="h-3.5 w-3.5" /> Reset authenticator
                </Button>
                <Button size="xs" variant="danger-outline" aria-label="Delete" onClick={() => setPending({ kind: 'delete', owner })}>
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </>
            )
          }
        />
      )}

      {pending !== null && (
        <OwnerConfirmDialog
          open={pending !== null}
          onClose={() => setPending(null)}
          title={title}
          description={pending && 'owner' in pending ? pending.owner.email : undefined}
          warning={
            pending?.kind === 'create' ? (
              <>
                They pair their own authenticator with a one-time enrolment code. It is emailed to them when
                the alert mail channel is set up; otherwise it is shown to you once, to pass on.
              </>
            ) : pending?.kind === 'reset' ? (
              <>They are signed out everywhere and must pair a new authenticator with a one-time code.</>
            ) : pending?.kind === 'delete' ? (
              <>
                This removes their access immediately and cannot be undone. To pause them instead,
                deactivate.
              </>
            ) : undefined
          }
          extraValid={!needsFields || (emailValid && nameValid)}
          confirmLabel={
            pending?.kind === 'create'
              ? 'Add owner'
              : pending?.kind === 'delete'
                ? 'Delete'
                : pending?.kind === 'reset'
                  ? 'Reset'
                  : pending?.kind === 'toggle'
                  ? pending.owner.isActive
                    ? 'Deactivate'
                    : 'Reactivate'
                  : 'Save'
          }
          destructive={pending?.kind === 'delete' || pending?.kind === 'toggle' || pending?.kind === 'reset'}
          action={({ totpCode, reason }) => {
            if (!pending) return Promise.reject(new Error('No action selected'));
            if (pending.kind === 'create') {
              return post('/platform/owners', { name, email, totpCode, reason });
            }
            const path = `/platform/owners/${encodeURIComponent(pending.owner.id)}`;
            if (pending.kind === 'edit') return put(path, { name, email, totpCode, reason });
            if (pending.kind === 'reset') return post(`${path}/reset-authenticator`, { totpCode, reason });
            if (pending.kind === 'toggle') {
              return put(path, { isActive: !pending.owner.isActive, totpCode, reason });
            }
            return del(path, { body: { totpCode, reason } });
          }}
          onDone={(result) => {
            void queryClient.invalidateQueries({ queryKey: ['platform-owners'] });
            const given = result as Partial<OwnerEnrolmentHandout> | undefined;
            if (given && given.enrolmentExpiresAt && pending) {
              setHandout({
                enrolmentCode: given.enrolmentCode ?? null,
                enrolmentEmailedTo: given.enrolmentEmailedTo ?? null,
                enrolmentExpiresAt: given.enrolmentExpiresAt,
                email: 'owner' in pending ? pending.owner.email : email,
              });
            }
            setPending(null);
          }}
          successMessage="Done."
        >
          {needsFields ? (
            <>
              <Field label="Full name">
                <Input value={name} onChange={(e) => setName(e.target.value)} />
              </Field>
              <Field label="Email">
                <Input
                  type="email"
                  autoComplete="off"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </Field>
            </>
          ) : null}
        </OwnerConfirmDialog>
      )}

      {handout && (
        <Dialog
          open
          onClose={() => setHandout(null)}
          title="Enrolment code"
          description={handout.email}
          footer={<Button onClick={() => setHandout(null)}>Done</Button>}
        >
          {handout.enrolmentCode ? (
            <div className="space-y-3">
              <div className="rounded-md border bg-muted/40 p-4 text-center font-mono text-lg tracking-widest">
                {handout.enrolmentCode}
              </div>
              <Alert variant="warning">
                This is not an authenticator key — do not type it into Google Authenticator. The owner opens the
                Owner sign-in page, enters their email, chooses “First sign-in? I have an enrolment code” and enters
                it there. That page then shows the real secret to add to the authenticator.
              </Alert>
              <Alert>
                Shown once. It works a single time and expires {dateTime(handout.enrolmentExpiresAt)}. Pass it on by
                another channel than this screen.
              </Alert>
            </div>
          ) : (
            <Alert>
              The code was emailed to {handout.enrolmentEmailedTo}. It expires {dateTime(handout.enrolmentExpiresAt)}.
            </Alert>
          )}
        </Dialog>
      )}
    </div>
  );
}
